import type { IntlShape } from 'react-intl'

import {
  CRUISE_PILL_ICON_MARKER_TYPE,
  CRUISE_PILL_MARKER_TYPE,
} from 'constants/map'
import { unique } from 'lib/array/arrayUtils'
import { fitBoundsWithExtraSpace, getBoundsFromMarkers } from 'lib/maps/mapboxMapUtils'
import type { MapHandle } from 'lib/maps/types'

export interface CruiseItineraryMapMarker extends App.MapMarker {
  type: typeof CRUISE_PILL_MARKER_TYPE | typeof CRUISE_PILL_ICON_MARKER_TYPE
  name: string
  locationHeading: string
}

interface GroupedCruisePort {
  port: App.CruisePort
  days: Array<number>
  ports: Array<number>
  isStart: boolean
  isEnd: boolean
}

function hasValidCruisePort(
  port?: App.CruisePort | null,
): port is App.CruisePort & { latitude: string; longitude: string } {
  if (!port?.latitude || !port.longitude) {
    return false
  }

  return Number.isFinite(Number(port.latitude)) && Number.isFinite(Number(port.longitude))
}

export function getCruiseItineraryMarkers(
  itinerary: Array<App.CruiseItineraryItem>,
  intl: IntlShape,
): Array<CruiseItineraryMapMarker> {
  const itineraryWithPorts = Object.values(
    itinerary.reduce<Record<string, App.CruiseItineraryItem>>((acc, item) => {
      if (!hasValidCruisePort(item.port)) {
        return acc
      }

      return {
        ...acc,
        [`${item.startDay}_${item.port.name}`]: item,
      }
    }, {}),
  )

  let portNumber = 0
  let lastPortName = ''

  const itineraryByPort = itineraryWithPorts.reduce<Record<string, GroupedCruisePort>>((acc, { port, startDay }, index) => {
    if (!hasValidCruisePort(port)) {
      return acc
    }

    const key = port.name
    const value = acc[key] || { port, days: [], ports: [], isStart: false, isEnd: false }

    const isStart = value.isStart || index === 0
    const isEnd = value.isEnd || index === itineraryWithPorts.length - 1
    const addPortCircle = lastPortName !== port.name && index > 0

    if (addPortCircle) {
      portNumber += 1
      lastPortName = port.name
    }

    return {
      ...acc,
      [key]: {
        ...value,
        isStart,
        isEnd,
        days: unique([...value.days, startDay]),
        ports: unique([...value.ports, portNumber]),
      },
    }
  }, {})

  return Object.keys(itineraryByPort).map((key) => {
    const { port, days, isStart, isEnd, ports } = itineraryByPort[key]

    let name = intl.formatMessage(
      {
        id: 'QRdNvLGAmyRc5GnqwLuD',
        defaultMessage: '{count, plural, one {Port} other {Ports}} {portNumbers}',
        description: 'Port marker label on cruise itinerary map in the cruise section',
      },
      { count: ports.length, portNumbers: ports.join(', ') },
    )
    let type: CruiseItineraryMapMarker['type'] = CRUISE_PILL_MARKER_TYPE

    if (isStart && isEnd) {
      name = intl.formatMessage({
        id: '0lyq5HtHdUmqrLKCkGyD',
        defaultMessage: 'Start & End',
        description: 'Label for combined start and end port on cruise itinerary map',
      })
      type = CRUISE_PILL_ICON_MARKER_TYPE
    } else if (isStart) {
      name = intl.formatMessage({
        id: 'JKSqHbTJv8qbdw2JZ7Ft',
        defaultMessage: 'Start',
        description: 'Label for start port on cruise itinerary map in the cruise section',
      })
      type = CRUISE_PILL_ICON_MARKER_TYPE
    } else if (isEnd) {
      name = intl.formatMessage({
        id: 'IoqVUjNIeVKrDtVMKb7g',
        defaultMessage: 'End',
        description: 'Label for end port on cruise itinerary map in the cruise section',
      })
      type = CRUISE_PILL_ICON_MARKER_TYPE
    }

    return {
      id: key,
      latitude: Number(port.latitude),
      longitude: Number(port.longitude),
      locationHeading: intl.formatMessage(
        {
          id: 'bTc0KCyae93HPxEfvzjn',
          defaultMessage: 'Day {days}: {portName}',
          description: 'Port tooltip on cruise itinerary map in the cruise offer or booking section',
        },
        { days: days.join(', '), portName: port.name },
      ),
      type,
      name,
    }
  })
}

export function fitCruiseMarkersOnMap(
  map: MapHandle | null,
  markers: Array<{ latitude: number, longitude: number }>,
) {
  if (!map || markers.length === 0) {
    return
  }

  if (markers.length === 1) {
    map.setViewport({
      longitude: markers[0].longitude,
      latitude: markers[0].latitude,
      zoom: 4,
    })
    return
  }

  const bounds = getBoundsFromMarkers(markers)
  if (!bounds) {
    return
  }

  fitBoundsWithExtraSpace(map, bounds)
}

export function focusCruisePortOnMap(
  map: MapHandle | null,
  port?: App.CruisePort | null,
) {
  if (!map || !hasValidCruisePort(port)) {
    return
  }

  map.setViewport({
    longitude: Number(port.longitude),
    latitude: Number(port.latitude),
    zoom: 5,
  }, {
    animate: true,
    durationMs: 500,
    essential: true,
  })
}
